import React, { useEffect, useRef, useState, useCallback } from "react";
import { Bell, MessageCircle, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  athleteCoachNotifications,
  athleteMarkAllNotificationsRead,
} from "../../lib/api";

/**
 * AthleteNotificationBell
 *
 * Header-mounted bell icon + dropdown that surfaces recent unread coach
 * replies in the athlete portal. Polls every 60s while the tab is
 * focused so coaches' replies show up even if the athlete didn't refresh
 * their email. Clicking a notification jumps to the athlete's program
 * view at the right week; the comment itself stays unread until the
 * athlete opens the workout thread (the existing GET
 * /api/athlete/me/workout-comments endpoint already marks it read).
 *
 * "Mark all read" clears the unread badge in one call without forcing
 * the athlete to click into each thread.
 */
const POLL_MS = 60 * 1000;

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.round(days / 7);
  return `${weeks}w ago`;
}

function snippet(body, max = 120) {
  if (!body) return "";
  const text = String(body).replace(/<[^>]*>/g, "").trim();
  if (text.length <= max) return text;
  return text.slice(0, max).trimEnd() + "…";
}

export default function AthleteNotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const data = await athleteCoachNotifications();
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      // Silent — bell just won't update; no point alerting the athlete.
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [refresh]);

  // Click-outside to close the dropdown.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const handleItemClick = (n) => {
    setOpen(false);
    // Optimistically drop this item so the badge updates immediately
    // — the actual server-side read state is set when the athlete opens
    // the workout's comment thread (existing GET /workout-comments side
    // effect), but waiting up to 60s for the next poll feels broken.
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    setTotal((t) => Math.max(0, t - 1));
    // Navigate to the program page at the right week. The existing
    // athlete program route reads ?week=N to scroll to a specific week.
    if (n.program_id) {
      const wo = n.workout_id ? `&workout=${encodeURIComponent(n.workout_id)}` : "";
      navigate(`/athlete/programs/${n.program_id}?week=${n.week_number || 1}${wo}`);
    }
  };

  const handleMarkAll = async () => {
    setLoading(true);
    try {
      await athleteMarkAllNotificationsRead();
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] focus:outline-none focus:ring-2 focus:ring-[var(--tir-burgundy)]/30 rounded-full"
        aria-label={`Notifications${total ? ` (${total} unread)` : ""}`}
        data-testid="athlete-notification-bell"
      >
        <Bell size={18} strokeWidth={2} />
        {total > 0 && (
          <span
            className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 bg-[var(--tir-burgundy)] text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none"
            data-testid="athlete-notification-badge"
          >
            {total > 9 ? "9+" : total}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 md:w-96 max-h-[28rem] bg-white border border-[var(--tir-border)] shadow-xl rounded-md overflow-hidden z-50"
          data-testid="athlete-notification-panel"
        >
          <div className="px-3 py-2 border-b border-[var(--tir-border)] flex items-center justify-between bg-[var(--tir-ivory)]">
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-ink)]">
              Coach replies
            </div>
            {total > 0 && (
              <button
                type="button"
                onClick={handleMarkAll}
                disabled={loading}
                className="text-[9px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)] disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="athlete-notification-mark-all"
              >
                <Check size={10} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-[var(--tir-border)]">
            {items.length === 0 ? (
              <div className="px-4 py-6 text-center text-[11px] text-[var(--tir-muted)]" data-testid="athlete-notification-empty">
                You&apos;re all caught up.
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleItemClick(n)}
                  className="block w-full text-left px-3 py-2.5 hover:bg-[var(--tir-ivory)] focus:bg-[var(--tir-ivory)] focus:outline-none"
                  data-testid={`athlete-notification-item-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    <MessageCircle size={12} className="text-[var(--tir-burgundy)] shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-burgundy)]">
                        {n.from_name} · {n.workout_title}
                      </div>
                      <div className="text-[10px] text-[var(--tir-muted)] mb-1">
                        {n.program_name} · W{n.week_number} {n.day_label}
                        {n.created_at && (
                          <span className="ml-1">· {relativeTime(n.created_at)}</span>
                        )}
                      </div>
                      <div className="text-[12px] text-[var(--tir-ink)] leading-snug">
                        {snippet(n.body)}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
